import dayjs from 'dayjs';
import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';
import { StatementTransactionService } from '/src/api';
import { LenderStatementTransactionService } from '/src/api/services/lender-statement-transaction-service';
import { Lender, Loan, Statement, StatementHeadlines, TLenderId, TLoanId, Transaction } from '/src/lib/models';
import { DataModelPluralType, MenuItemType, UserRole } from '/src/lib/types';

export interface StatementStoreProps {
  /**
   * List of Statement transactions
   */
  transactions?: Transaction[];
  /**
   * Statement headlines
   */
  headlines?: StatementHeadlines;
  /**
   * List of Loans
   */
  loans?: Record<string, any>[];
  /**
   * Statement totals
   */
  totals?: Record<string, number>;
  /**
   * List of Statements
   */
  statements?: Statement[];
  /**
   * Currently selected Statement
   */
  currentStatement?: Statement;
  /**
   * Currently selected Lender
   */
  currentLender?: Lender;
  /**
   * Whether to show all Adjustments
   */
  allAdjustments?: boolean;
  /**
   * Total available credit
   */
  totalAvailableCredit?: number;
  /**
   * Total currently available credit
   */
  totalCurrentAvailableCredit?: number;
  /**
   * Total beginning principal balance
   */
  totalBeginPrincipalBalance?: number;
  /**
   * Total ending principal balance
   */
  totalEndingPrincipalBalance?: number;
}

export class StatementStore extends BaseStore implements StatementStoreProps {
  private didInitMenu = false;
  transactions?: Transaction[];
  headlines?: StatementHeadlines;
  loans?: Record<string, any>[];
  totals?: Record<string, number>;
  statements?: Statement[];
  currentStatement?: Statement;
  currentLender?: Lender;
  allAdjustments?: boolean;

  totalAvailableCredit?: number;
  totalCurrentAvailableCredit?: number;
  totalBeginPrincipalBalance?: number;
  totalEndingPrincipalBalance?: number;

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      transactions: observable,
      headlines: observable,
      loans: observable,
      totals: observable,
      statements: observable,
      currentStatement: observable,
      currentLender: observable,
      allAdjustments: observable,
      totalAvailableCredit: observable,
      totalCurrentAvailableCredit: observable,
      totalBeginPrincipalBalance: observable,
      totalEndingPrincipalBalance: observable,
    });
  }

  /**
   * Adds Statement nav menu items to the app header.
   */
  initMenuItems = () => {
    if (this.didInitMenu || !this.rootStore.userStore.user) return;

    // Add menu items
    if (this.rootStore.userStore.isAdmin || this.rootStore.userStore.isLender) {
      this.rootStore.menuStore.addMenuItem(
        'topbar',
        'Statements',
        'statements',
        MenuItemType.Dropdown,
        'statements',
        false,
        [UserRole.Admin, UserRole.Investor],
        2
      );
      this.rootStore.menuStore.addSubMenuItem('topbar', 'statements', 'Deal Statements', 'statements');
      this.rootStore.menuStore.addSubMenuItem(
        'topbar',
        'statements',
        'Investor Statements',
        'statements/investors',
        'statements/investors',
        false,
        [UserRole.Admin, UserRole.Company]
      );
    } else {
      this.rootStore.menuStore.addMenuItem(
        'topbar',
        'Statements',
        'statements',
        MenuItemType.Item,
        '/statements',
        false,
        [UserRole.Admin, UserRole.Company]
      );
    }

    runInAction(() => (this.didInitMenu = true));
  };

  /**** Utility Methods ****/

  /**
   * Retrieves and formats Loan Statements from a list of Transactions.
   *
   * @param transactions - An array of Transactions to parse
   * @returns The formatted Loan Statements
   */
  formatLoanStatements = (transactions: Transaction[]) => {
    return transactions.filter((transaction) => transaction.__t === DataModelPluralType.Statement) as Statement[];
  };

  /**
   * Retrieves and formats Lender Statements from a list of Transactions.
   *
   * @param transactions - An array of Transactions to parse
   * @returns The formatted Lender Statements
   */
  formatLenderStatements = (transactions: Transaction[]) => {
    const statementTxns = transactions
      .filter((transaction) => transaction.__t === DataModelPluralType.Statement)
      .sort((a, b) => new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()) as Statement[];

    // Prepare statements array
    const statements: Statement[] = [];
    let previousDate: string | undefined;
    let txnDate: string | undefined;

    // Loop through transactions looking for statement txn dates
    for (const statement of statementTxns) {
      txnDate = dayjs(statement.effectiveDate).format('YYYY-MM');
      // If this month hasn't already been added...
      if (txnDate !== previousDate) {
        previousDate = txnDate;
        statements.push(statement);
      }
    }

    return statements;
  };

  /**** Loan Statement CRUD ****/

  /**
   * Fetches and stores Loan Statement data for the given Loan.
   *
   * @param loanId - ID of the Loan to fetch Statements for
   */
  queryLoanStatements = async (loanId: TLoanId) => {
    if (!loanId) return;

    const { transactions } = await StatementTransactionService.query({ loanId });

    const statements = this.formatLoanStatements(transactions);

    runInAction(() => {
      this.statements = statements;
    });
  };

  /**
   * Fetches a Loan Statement and sets associated transactions, headers, and totals.
   *
   * @param loanId - ID of the Loan to fetch Statements for
   * @param month - Statement month to fetch. Value should be a string in the format 'YYYY-MM'
   */
  getLoanStatement = async (loanId: TLoanId, month: string) => {
    const m = dayjs(month, 'YYYY-MM');
    const startDate = m.startOf('month');
    const endDate = m.endOf('month');

    await Promise.all([
      this.rootStore.statementTransactionsStore.query({
        loanId,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      }),
      this.rootStore.statementHeadlinesStore.get({
        loanId,
        date: startDate.toISOString(),
      }),
    ]);

    runInAction(() => {
      this.headlines = this.rootStore.statementHeadlinesStore.statementHeadlines;
      this.transactions = this.rootStore.statementTransactionsStore.transactions;
      this.totals = this.rootStore.statementTransactionsStore.totals;
      this.statements = this.rootStore.statementTransactionsStore.statements;
    });
  };

  /**** Lender Statement CRUD ****/

  /**
   * Fetches and stores Lender Statement data for the given Lender.
   *
   * @param lenderId - ID of the Lender to fetch Statements for
   */
  queryLenderStatements = async (lenderId: TLenderId) => {
    if (!lenderId) return;

    const { transactions, totals } = await LenderStatementTransactionService.query({ lenderId });

    runInAction(() => {
      this.transactions = transactions;
      this.totals = totals;
      this.statements = this.formatLenderStatements(transactions);
    });
  };

  /**
   * Fetches a Lender Statement and sets associated transactions, headers, and totals.
   *
   * @param lenderId - ID of the Lender to fetch Statements for
   * @param month - Statement month to fetch. Value should be a string in the format 'YYYY-MM'
   */
  getLenderStatement = async (lenderId: TLenderId, month: string) => {
    const m = dayjs(month, 'YYYY-MM');
    const startDate = m.startOf('month');
    const endDate = m.endOf('month');

    const [lender] = await Promise.all([
      this.rootStore.lenderStore.get(lenderId),
      this.rootStore.lenderStatementTransactionsStore.query({
        lenderId,
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      }),
      this.rootStore.lenderStatementHeadlinesStore.get({
        lenderId,
        date: startDate.toISOString(),
      }),
    ]);

    const transactions = this.rootStore.lenderStatementTransactionsStore.transactions ?? [];
    const totals = this.rootStore.lenderStatementTransactionsStore.totals;

    let totalAvailableCredit = 0;
    let totalCurrentAvailableCredit = 0;
    let totalBeginPrincipalBalance = 0;
    let totalEndingPrincipalBalance = 0;

    // Get participated loans list
    const loans: Partial<Loan>[] = [];

    const txnSorter = (a: any, b: any) => new Date(a.sequenceNumber) - new Date(b.sequenceNumber);

    if (lender && lender.participations.length) {
      for (const item in lender.participations) {
        const listItem: any = {};

        const participation = lender.participations[item];

        // Get the loan name
        const loanTxns = [];
        let beginPrincipalBalance = 0;
        let endPrincipalBalance = 0;
        let currentAvailableCredit = 0;
        let availableCredit = 0;
        const totals = {
          advanceAmount: 0,
          feesCharged: 0,
          paymentAmount: 0,
          feesPaid: 0,
          interestPaid: 0,
          principalPaid: 0,
        };

        if (transactions.length) {
          // Sort the transactions
          transactions.sort(txnSorter);

          // Go through transactions and pull the ones for that loan
          for (const transaction of transactions) {
            if (transaction.loanName === participation.loan.name) {
              // Calculate loan totals
              if (transaction.advanceAmount) totals.advanceAmount += transaction.advanceAmount;
              if (transaction.fees) totals.feesCharged += transaction.fees;
              if (transaction.paymentAmount) totals.paymentAmount += transaction.paymentAmount;
              if (transaction.appliedToFees) totals.feesPaid += transaction.appliedToFees;
              if (transaction.appliedToInterest) totals.interestPaid += transaction.appliedToInterest;
              if (transaction.appliedToPrincipal) totals.principalPaid += transaction.appliedToPrincipal;

              loanTxns.push(transaction);
            }
          }
        }

        if (loanTxns.length) {
          // Get the earliest transaction's principal balance
          beginPrincipalBalance = loanTxns[0].principalBalance;

          // Get the latest transaction's final principal balance
          endPrincipalBalance =
            loanTxns[loanTxns.length - 1].newPrincipalBalance ?? loanTxns[loanTxns.length - 1].principalBalance;

          // Get the current available credit for this loan
          availableCredit = loanTxns[loanTxns.length - 1].assetValue * participation.loan.advanceRate;
          currentAvailableCredit = availableCredit - loanTxns[loanTxns.length - 1].principalBalance;

          // Loan totals
          listItem.totals = totals;

          // Statement totals
          totalAvailableCredit += availableCredit;
          totalCurrentAvailableCredit += currentAvailableCredit;
          totalBeginPrincipalBalance += beginPrincipalBalance;
          totalEndingPrincipalBalance += endPrincipalBalance;
        }

        // Add loan name
        listItem.name = participation.loan.name;

        // Store loan txns in loans array
        listItem.loanTxns = loanTxns;

        loans.push(listItem);
      }
    }

    runInAction(() => {
      this.headlines = this.rootStore.lenderStatementHeadlinesStore.lenderStatementHeadlines;
      this.statements = this.rootStore.lenderStatementTransactionsStore.statements;
      this.allAdjustments = this.rootStore.lenderStatementTransactionsStore.allAdjustments;
      this.transactions = transactions;
      this.totals = totals;
      this.loans = loans;
      this.currentLender = lender;
      this.totalAvailableCredit = totalAvailableCredit;
      this.totalCurrentAvailableCredit = totalCurrentAvailableCredit;
      this.totalBeginPrincipalBalance = totalBeginPrincipalBalance;
      this.totalEndingPrincipalBalance = totalEndingPrincipalBalance;
    });
  };
}
